import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchWorknumberApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { mapHistory } from "./worknumber.mapping";
import type { WorkNumberHistory } from "./worknumber.types";
import { worknumberKeys } from "./worknumberKeys";

/** Input for a Work Number pull — the applicant to pull work history for. */
export interface PullWorknumberInput {
  approvalId: string;
  applicantId: string;
}

/**
 * Mutation hook for pulling purchased employer info (the button click). Backed by
 * WorkbenchWorknumberApi.pullWorkNumber (POST), which pulls + persists the
 * applicant's income verification at recap-service. A per-attempt
 * `Idempotency-Key` dedups accidental double-submits. On success the mapped
 * history is written straight into the history query cache (no extra fetch), so
 * the results table renders immediately.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function usePullWorknumber() {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async ({ approvalId, applicantId }: PullWorknumberInput): Promise<WorkNumberHistory> => {
      if (shouldForceExit) {
        return {} as WorkNumberHistory;
      }
      const idempotencyKey = globalThis.crypto?.randomUUID?.();
      const response = await workbenchWorknumberApi.pullWorkNumber(approvalId, applicantId, idempotencyKey);
      return mapHistory(response.data);
    },
    onSuccess: (history, { approvalId, applicantId }) => {
      queryClient.setQueryData<WorkNumberHistory>(worknumberKeys.history({ approvalId, applicantId }), history);
    },
  });
}
